import { select, call, takeLatest, put, all } from 'redux-saga/effects';
import { ReactNode } from 'react';
import { IncentiveActions } from '../constants';
import { IncentiveApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteIncentiveAction,
    GetIncentiveAction,
    GetIncentivesAction,
    GetProvidersIncentivesAction,
    PersistIncentiveAction,
    GetIncentivesEntitiesAction,
} from '../actions/incentive-actions';
import sortingService from '../../services/sorting';
import {
    failureType,
    handleError,
    requestType,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
    successType,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { groupIncentive } from 'src/helpers/incentive/incentive';
import { groupDataToCsv } from '../../helpers/incentive/groupCsv';
import { exportToCsv } from '../../services/exports/csv';
import { GroupIncentive } from '../../helpers/incentive/GroupIncentive';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { changeToZeroMoment } from '../../helpers/timeHelpers';

function* handleFetchIncentives(action: GetIncentivesAction) {
    const oldData = yield select((state: StoreState) => state.incentives.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.incentiveDate) {
        filterProps = {
            ...filters,
            ...filterProps?.incentiveDate,
        };
        delete filterProps.incentiveDate;
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        IncentiveApi.getIncentives({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchIncentiveCsv(action: GetIncentivesAction) {
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.incentiveDate) {
        filterProps = {
            ...filters,
            ...filterProps?.incentiveDate,
        };
        delete filterProps.incentiveDate;
    }

    yield resolveApiCall(
        action,
        () =>
            IncentiveApi.getIncentives({
                ...filterProps,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage: 5000,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),

        function (data) {
            const groupCsvData = groupDataToCsv(data.payload);
            exportToCsv('received_bonuses.csv', groupCsvData);
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchIncentiveInFile(action: GetIncentivesAction) {
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: any = { ...filters };

    if (filterProps?.incentiveDate) {
        filterProps = {
            ...filters,
            ...filterProps?.incentiveDate,
        };
        delete filterProps.incentiveDate;
    }

    yield put({ type: requestType(IncentiveActions.FETCH_INCENTIVES_IN_FILE) });

    try {
        const response = yield call(IncentiveApi.getIncentives, {
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage: 5000,
            pagination: itemsPerPage === -1 ? 0 : 1,
        });

        const zip = new JSZip();

        const tasks = response.data.map((item: any) =>
            call(function* () {
                if (!item.attach) return;

                const url = item.attach.replace('https://localhost', 'http://localhost:10100');
                let filename = `${item.provider.fullName}_from_${changeToZeroMoment(
                    item.periodStart,
                    'D_M_Y',
                )}_to_${changeToZeroMoment(item.periodEnd, 'D_M_Y')}_rb${item.id}.csv`;
                filename = filename.replace(/[\s-]/g, '');

                try {
                    const fileResponse: Response = yield call(fetch, url);
                    const blob: Blob = yield call([fileResponse, fileResponse.blob]);
                    const arrayBuffer = yield call([blob, blob.arrayBuffer]);

                    zip.file(filename, arrayBuffer);
                } catch (err) {
                    console.error(`Failed to fetch file from ${url}`, err);
                }
            }),
        );

        yield all(tasks);

        const zipBlob = yield call([zip, zip.generateAsync], { type: 'blob' });
        yield call(saveAs, zipBlob, 'received_bonuses.zip');

        yield put({ type: successType(IncentiveActions.FETCH_INCENTIVES_IN_FILE) });
    } catch (error) {
        yield put({
            type: failureType(IncentiveActions.FETCH_INCENTIVES_IN_FILE),
            error: error.message || 'Unknown error',
        });

        handleError(error);
    }
}
function* handleFetchIncentiveInFileOneByOne(action: GetIncentivesAction) {
    const { filters, sorting, page, itemsPerPage } = action.payload;

    // Manually dispatch request
    yield put({ type: requestType(IncentiveActions.FETCH_INCENTIVES_IN_FILE) });

    try {
        const response = yield call(IncentiveApi.getIncentives, {
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage: 5000,
            pagination: itemsPerPage === -1 ? 0 : 1,
        });

        const zip = new JSZip();

        for (let i = 0; i < response.data.length; i++) {
            const item = response.data[i];
            if (item.attach) {
                const url = item.attach.replace('https://localhost', 'http://localhost:10100');
                let filename = `${item.provider.fullName}_from_${changeToZeroMoment(
                    item.periodStart,
                    'D_M_Y',
                )}_to_${changeToZeroMoment(item.periodEnd, 'D_M_Y')}_p${item.id}.csv`;
                filename = filename.replace(/[\s-]/g, '');

                try {
                    const fileResponse: Response = yield call(fetch, url);
                    const blob: Blob = yield call([fileResponse, fileResponse.blob]);
                    const arrayBuffer = yield call([blob, blob.arrayBuffer]);

                    zip.file(filename, arrayBuffer);
                } catch (err) {
                    console.error(`Failed to fetch file from ${url}`, err);
                }
            }
        }

        const zipBlob = yield call([zip, zip.generateAsync], { type: 'blob' });
        yield call(saveAs, zipBlob, 'received_bonuses.zip');

        // Now dispatch success only AFTER the zip is saved
        yield put({ type: successType(IncentiveActions.FETCH_INCENTIVES_IN_FILE) });
    } catch (error) {
        yield put({
            type: failureType(IncentiveActions.FETCH_INCENTIVES_IN_FILE),
            error: error.message || 'Unknown error',
        });

        handleError(error);
    }
}

function* handleFetchIncentivesEntities(action: GetIncentivesEntitiesAction) {
    const { id } = action.payload;
    yield put({
        type: requestType(IncentiveActions.FETCH_INCENTIVES_ENTITIES),
    });
    let resData: { [key: string]: string | ReactNode }[] = [];

    const p1 = IncentiveApi.getIncentiveBonuses(id, {});
    const objKeys: any = {
        0: 'incentive_bonuses',
    };

    const dataProm = yield all([p1]);

    dataProm.forEach((it: any, key: number) => {
        const dataCurrent = GroupIncentive({ type: objKeys[key], list: it.data });
        resData = resData.concat(dataCurrent);
    });

    const actionResult = {
        payload: resData,
        type: successType(IncentiveActions.FETCH_INCENTIVES_ENTITIES),
    };

    yield put(actionResult);
}

function* handleGetProvidersIncentive(action: GetProvidersIncentivesAction) {
    //const oldData = yield select((state: StoreState) => state.incentives.data);
    const { payload } = action;

    yield resolveApiCall(
        action,
        () =>
            IncentiveApi.getProvidersIncentives({
                page: 1,
                ...payload,
            }),
        function () {
            /* if (onSuccess) {
                onSuccess();
            }*/
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchIncentive(action: GetIncentiveAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => IncentiveApi.getIncentive(id));
}

function* handleDeleteIncentive(action: DeleteIncentiveAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => IncentiveApi.deleteIncentive(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistIncentive(action: PersistIncentiveAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    const groupedData = groupIncentive(restData);

    console.log(groupedData);

    yield resolveApiCall(
        action,
        () => (id ? IncentiveApi.updateIncentive(id, restData) : IncentiveApi.createIncentive(groupedData)),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.INCENTIVES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(IncentiveActions.FETCH_INCENTIVES, handleFetchIncentives);
    yield takeLatest(IncentiveActions.FETCH_INCENTIVES_CSV, handleFetchIncentiveCsv);
    yield takeLatest(IncentiveActions.FETCH_INCENTIVES_IN_FILE, handleFetchIncentiveInFile);
    yield takeLatest(IncentiveActions.FETCH_INCENTIVES_ENTITIES, handleFetchIncentivesEntities);
    yield takeLatest(IncentiveActions.FETCH_INCENTIVE, handleFetchIncentive);
    yield takeLatest(IncentiveActions.DELETE_INCENTIVE, handleDeleteIncentive);
    yield takeLatest(IncentiveActions.PERSIST_INCENTIVE, handlePersistIncentive);
    yield takeLatest(IncentiveActions.FETCH_INCENTIVES_PROVIDERS, handleGetProvidersIncentive);
}
